import { filterOption, ProtectPlusDeviceModal, type DeviceOption } from '@/entities/device';
import { ConfirmModal, CustomButton, CustomSelect, RadioGroup } from '@/shared/ui/components';
import { closeIconV2 } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { zeroPad } from '@/shared/lib';
import { Drawer } from 'antd';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { generatePatient, renderNotFoundContent } from './handler';
import { useAssignDeviceDrawer } from './useAssignDeviceDrawer';
import type { AssignDeviceDrawerProps } from './assignDeviceDrawer.types';
import './assignDeviceDrawer.scss';

const AssignDeviceDrawer = ({
  visible,
  handleClickCloseButton,
  handleAssignDeviceDrawerClick,
  study,
}: AssignDeviceDrawerProps) => {
  const {
    state,
    deviceModelList,
    deviceIdSelectRef,
    hasChangedDevice,
    handleDeviceTypeChange,
    handleDeviceIdDropdownVisibleChange,
    handleDeviceIdSearch,
    handleDeviceChange,
    handleRightButtonClickDeviceErrorModal,
    toggleDisplayWarningModal,
    handleProtectPlusDeviceModalClose,
    onClickCancelReturnedDeviceModal,
    onClickConfirmReturnedDeviceModal,
    handleAssignClick,
  } = useAssignDeviceDrawer({ visible, handleAssignDeviceDrawerClick, study });

  return (
    <>
      <ConfirmModal
        isOpen={state.isOpenDeviceErrorModal}
        onClickRightButton={handleRightButtonClickDeviceErrorModal}
        rightButtonName={t`Got It`}
        title={t`Cannot select this device`}
        question={state.deviceErrorModalMessage}
      />
      <ConfirmModal
        isOpen={state.isOpenWarningModal}
        onClickRightButton={() => {
          toggleDisplayWarningModal('firmware');
          if (hasChangedDevice.current) {
            hasChangedDevice.current = false;
            if (deviceIdSelectRef.current) {
              deviceIdSelectRef.current.focusAndOpenSelectInput();
            }
          }
        }}
        rightButtonName={t`Okay`}
        title={t`Firmware update required`}
        question={t`This study type is not supported by the current firmware of this device.Please update the firmware to its latest version.`}
      />
      <ConfirmModal
        isOpen={state.isOpenWarningAutoEventModal}
        onClickRightButton={() => {
          toggleDisplayWarningModal('auto-event');
          if (hasChangedDevice.current) {
            hasChangedDevice.current = false;
            if (deviceIdSelectRef.current) {
              deviceIdSelectRef.current.focusAndOpenSelectInput();
            }
          }
        }}
        rightButtonName={t`Okay`}
        title={t`Feature not available`}
        question={t`Please contact your sales representative to enable this feature.`}
      />
      <ConfirmModal
        className="warning-modal returned-device-modal"
        isRedBtn={false}
        isBoldLeftBtn
        isOpen={state.isOpenReturnedDeviceModal}
        isFilledRightButton
        rightButtonName={t`Continue`}
        leftButtonName={t`Cancel`}
        onClickLeftButton={onClickCancelReturnedDeviceModal}
        onClickRightButton={onClickConfirmReturnedDeviceModal}
        title={t`Confirm returned device`}
        isSaving={state.isSavingReturnedDeviceModal}
        customContent={
          <div className="returned-device-modal__content">
            {state.isSavingReturnedDeviceModal && <div className="saving-background" />}
            <span>
              {t`By clicking “Continue” implies that you have confirmed the device has been returned with all the accompanying items.Are you sure you want to proceed ? `}
            </span>
            {state.returnedDeviceDataModal?.isViewGotoFleetManagement && (
              <div className="returned-device-modal__action">
                {t`Need more consideration ? `}{' '}
                <a
                  target="_blank"
                  onClick={onClickCancelReturnedDeviceModal}
                  href={`/studies/${state.returnedDeviceDataModal?.studyId}`}
                  rel="noreferrer"
                >
                  {t`Go to device's previous study details.`}
                </a>
              </div>
            )}
          </div>
        }
      />
      <ProtectPlusDeviceModal
        isOpen={state.isOpenProtectPlusDeviceModal}
        onClickCancelButton={handleProtectPlusDeviceModalClose}
        onClickConfirmButton={handleProtectPlusDeviceModalClose}
      />

      <Drawer
        id="assign-device-drawer-id"
        aria-label={t`Assign device`}
        rootClassName="custom-drawer assign-device-drawer"
        width={500}
        closable={false}
        open={visible}
      >
        <div className="drawer-title-container">
          <span className="drawer-title">{t`Assign device`}</span>
          <CustomButton
            id="close-btn"
            style="neutral"
            type="tertiary"
            size="small"
            icon={<ReactSVG src={closeIconV2} />}
            disabled={state.isAssigningDevice}
            onClick={handleClickCloseButton}
            ariaLabel={t`Close`}
          />
        </div>
        <div className="drawer-body-container">
          <div className="study-information-container">
            <div className="label">{t`Study information`}</div>
            <div className="content-container">
              <div className="content-item">
                <div className="content-item-label">{`${t`Study ID`}:`}</div>
                <div className="content-item-value">
                  {zeroPad(study?.friendlyId) || DOUBLE_HYPHEN_BULLET}
                </div>
              </div>
              <div className="content-item">
                <div className="content-item-label">{`${t`Study type`}:`}</div>
                <div className="content-item-value">{study?.studyType || DOUBLE_HYPHEN_BULLET}</div>
              </div>
              <div className="content-item">
                <div className="content-item-label">{`${t`Patient`}:`}</div>
                <div className="content-item-value">{generatePatient(study?.info?.patient)}</div>
              </div>
            </div>
          </div>
          <div className="horizontal-divider" />
          <div className="device-model-container">
            <div className="label">{t`Device type`}</div>
            <div className="list-device-types">
              <RadioGroup
                value={state.model}
                name="model"
                className="radio-input device-model-input"
                onChange={handleDeviceTypeChange}
                options={deviceModelList}
                size="small"
              />
            </div>
          </div>
          <div className="device-id-select-input-container-wrapper">
            <CustomSelect
              ref={deviceIdSelectRef}
              className="device-id-select"
              title={t`Device ID`}
              aria-label={t`Device ID`}
              allowClear={false}
              popupClassName="device-id-list-select"
              name="deviceId"
              size="middle"
              searchValue={state.searchDeviceId}
              onSearch={handleDeviceIdSearch}
              onDropdownVisibleChange={handleDeviceIdDropdownVisibleChange}
              options={state.devicesOptions}
              value={
                state.isLoadingDevice
                  ? t`Loading...`
                  : state.device?.isHighSamplingRate
                    ? state.device?.value
                    : state.device || null
              }
              filterOption={(inputValue, option) =>
                filterOption(inputValue, (option ?? null) as DeviceOption | null)
              }
              onChange={handleDeviceChange as any}
              disabled={state.isLoadingDevice}
              placeholder={state.isLoadingDevice ? t`Loading...` : t`Search & Select a device ID`}
              isError={!!state.errorObject?.deviceId}
              errorMessage={state.errorObject?.deviceId}
              notFoundContent={renderNotFoundContent({
                isSearchingDevice: state.isSearchingDevice,
                searchDeviceId: state.searchDeviceId,
              })}
              infoMessage={
                state.device?.isHighSamplingRate
                  ? t`This device is operating at a high sampling rate.`
                  : undefined
              }
            />
          </div>
          <div className="bottom-button-row">
            <CustomButton
              id="cancel-btn"
              style="neutral"
              type="outline"
              onClick={handleClickCloseButton}
              styles={{ minWidth: 80 }}
              disabled={state.isAssigningDevice}
            >
              {t`Cancel`}
            </CustomButton>
            <CustomButton
              id="save-btn"
              onClick={handleAssignClick}
              styles={{ minWidth: 80 }}
              loading={state.isAssigningDevice}
              disabled={!state.device || state.isAssigningDevice}
            >
              {t`Assign`}
            </CustomButton>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default AssignDeviceDrawer;
